import { clsx, type ClassValue } from 'clsx';
import { type GetSrcArgs, defaultGetSrc } from 'openimg/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormAction, useNavigation } from 'react-router';
import { useSpinDelay } from 'spin-delay';
import { extendTailwindMerge } from 'tailwind-merge';
import { extendedTheme } from './extended-theme.ts';

export function getUserImgSrc(objectKey?: string | null) {
  return objectKey
    ? `/resources/images?objectKey=${encodeURIComponent(objectKey)}`
    : '/img/user.png';
}

export function getNoteImgSrc(objectKey: string) {
  return `/resources/images?objectKey=${encodeURIComponent(objectKey)}`;
}

export function getImgSrc({
  height,
  optimizerEndpoint,
  src,
  width,
  fit,
  format,
}: GetSrcArgs) {
  // We customize getImgSrc so our src looks nice like this:
  // /resources/images?objectKey=...&h=...&w=...&fit=...&format=...
  // instead of this:
  // /resources/images?src=%2Fresources%2Fimages%3FobjectKey%3D...%26w%3D...%26h%3D...
  if (src.startsWith(optimizerEndpoint)) {
    const [endpoint, query] = src.split('?');
    const searchParams = new URLSearchParams(query);
    searchParams.set('h', height.toString());
    searchParams.set('w', width.toString());
    if (fit) {
      searchParams.set('fit', fit);
    }
    if (format) {
      searchParams.set('format', format);
    }
    return `${endpoint}?${searchParams.toString()}`;
  }
  return defaultGetSrc({ height, optimizerEndpoint, src, width, fit, format });
}

export function getErrorMessage(error: unknown) {
  if (typeof error === 'string') return error;
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }
  console.error('Unable to get error message for error', error);
  return 'Unknown Error';
}

function formatColors() {
  const colors = [];
  for (const [key, color] of Object.entries(extendedTheme.colors)) {
    if (typeof color === 'string') {
      colors.push(key);
    } else {
      const colorGroup = Object.keys(color).map((subKey) =>
        subKey === 'DEFAULT' ? '' : subKey,
      );
      colors.push({ [key]: colorGroup });
    }
  }
  return colors;
}

const customTwMerge = extendTailwindMerge<string, string>({
  extend: {
    theme: {
      colors: formatColors(),
      borderRadius: Object.keys(extendedTheme.borderRadius),
    },
    classGroups: {
      'font-size': [
        {
          text: Object.keys(extendedTheme.fontSize),
        },
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return customTwMerge(clsx(inputs));
}

export function getDomainUrl(request: Request) {
  if (process.env.APP_ORIGIN) return process.env.APP_ORIGIN;
  // Fly passes client-set X-Forwarded-* headers through, so never read them.
  const protocol = request.headers.get('Fly-Forwarded-Proto') ?? 'http';
  const host = request.headers.get('host') ?? new URL(request.url).host;
  return `${protocol}://${host}`;
}

export function getReferrerRoute(request: Request) {
  // spelling errors and whatever makes this annoyingly inconsistent
  // in my own testing, `referer` returned the right value, but 🤷‍♂️
  const referrer =
    request.headers.get('referer') ??
    request.headers.get('referrer') ??
    request.referrer;
  const domain = getDomainUrl(request);
  if (referrer?.startsWith(domain)) {
    return referrer.slice(domain.length);
  } else {
    return '/';
  }
}

/**
 * Merge multiple headers objects into one (uses set so headers are overridden)
 */
export function mergeHeaders(
  ...headers: Array<ResponseInit['headers'] | null | undefined>
) {
  const merged = new Headers();
  for (const header of headers) {
    if (!header) continue;
    for (const [key, value] of new Headers(header).entries()) {
      merged.set(key, value);
    }
  }
  return merged;
}

/**
 * Combine multiple header objects into one (uses append so headers are not overridden)
 */
export function combineHeaders(
  ...headers: Array<ResponseInit['headers'] | null | undefined>
) {
  const combined = new Headers();
  for (const header of headers) {
    if (!header) continue;
    for (const [key, value] of new Headers(header).entries()) {
      combined.append(key, value);
    }
  }
  return combined;
}

/**
 * Combine multiple response init objects into one (uses combineHeaders)
 */
export function combineResponseInits(
  ...responseInits: Array<ResponseInit | null | undefined>
) {
  let combined: ResponseInit = {};
  for (const responseInit of responseInits) {
    combined = {
      ...responseInit,
      headers: combineHeaders(combined.headers, responseInit?.headers),
    };
  }
  return combined;
}

/**
 * Returns true if the current navigation is submitting the current route's
 * form. Defaults to the current route's form action and method POST.
 *
 * Defaults state to 'non-idle'
 *
 * NOTE: the default formAction will include query params, but the
 * navigation.formAction will not, so don't use the default formAction if you
 * want to know if a form is submitting without specific query params.
 */
export function useIsPending({
  formAction,
  formMethod = 'POST',
  state = 'non-idle',
}: {
  formAction?: string;
  formMethod?: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE';
  state?: 'submitting' | 'loading' | 'non-idle';
} = {}) {
  const contextualFormAction = useFormAction();
  const navigation = useNavigation();
  const isPendingState =
    state === 'non-idle'
      ? navigation.state !== 'idle'
      : navigation.state === state;
  return (
    isPendingState &&
    navigation.formAction === (formAction ?? contextualFormAction) &&
    navigation.formMethod === formMethod
  );
}

/**
 * This combines useSpinDelay (from https://npm.im/spin-delay) and useIsPending
 * from our own utilities to give you a nice way to show a loading spinner for
 * a minimum amount of time, even if the request finishes right after the delay.
 *
 * This avoids a flash of loading state regardless of how fast or slow the
 * request is.
 */
export function useDelayedIsPending({
  formAction,
  formMethod,
  delay = 400,
  minDuration = 300,
}: Parameters<typeof useIsPending>[0] &
  Parameters<typeof useSpinDelay>[1] = {}) {
  const isPending = useIsPending({ formAction, formMethod });
  const delayedIsPending = useSpinDelay(isPending, {
    delay,
    minDuration,
  });
  return delayedIsPending;
}

function callAll<Args extends Array<unknown>>(
  ...fns: Array<((...args: Args) => unknown) | undefined>
) {
  return (...args: Args) => fns.forEach((fn) => fn?.(...args));
}

/**
 * Use this hook with a button and it will make it so the first click sets a
 * `doubleCheck` state to true, and the second click will actually trigger the
 * `onClick` handler. This allows you to have a button that can be like a
 * "are you sure?" experience for the user before doing destructive operations.
 */
export function useDoubleCheck() {
  const [doubleCheck, setDoubleCheck] = useState(false);

  function getButtonProps(
    props?: React.ButtonHTMLAttributes<HTMLButtonElement>,
  ) {
    const onBlur: React.ButtonHTMLAttributes<HTMLButtonElement>['onBlur'] =
      () => setDoubleCheck(false);

    const onClick: React.ButtonHTMLAttributes<HTMLButtonElement>['onClick'] =
      doubleCheck
        ? undefined
        : (e) => {
            e.preventDefault();
            setDoubleCheck(true);
          };

    const onKeyUp: React.ButtonHTMLAttributes<HTMLButtonElement>['onKeyUp'] = (
      e,
    ) => {
      if (e.key === 'Escape') {
        setDoubleCheck(false);
      }
    };

    return {
      ...props,
      onBlur: callAll(onBlur, props?.onBlur),
      onClick: callAll(onClick, props?.onClick),
      onKeyUp: callAll(onKeyUp, props?.onKeyUp),
    };
  }

  return { doubleCheck, getButtonProps };
}

/**
 * Simple debounce implementation
 */
function debounce<Callback extends (...args: Parameters<Callback>) => void>(
  fn: Callback,
  delay: number,
) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<Callback>) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      fn(...args);
    }, delay);
  };
}

/**
 * Debounce a callback function
 */
export function useDebounce<
  Callback extends (...args: Parameters<Callback>) => ReturnType<Callback>,
>(callback: Callback, delay: number) {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  });
  return useMemo(
    () =>
      debounce(
        (...args: Parameters<Callback>) => callbackRef.current(...args),
        delay,
      ),
    [delay],
  );
}

export async function downloadFile(url: string, retries: number = 0) {
  const MAX_RETRIES = 3;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image with status ${response.status}`);
    }
    const contentType = response.headers.get('content-type') ?? 'image/jpg';
    const arrayBuffer = await response.arrayBuffer();
    const file = new File([arrayBuffer], 'downloaded-file', {
      type: contentType,
    });
    return file;
  } catch (e) {
    if (retries > MAX_RETRIES) throw e;
    return downloadFile(url, retries + 1);
  }
}

// utils/quarterGenerator.ts
interface QuarterOption {
  label: string; // e.g. "2023 Q1"
  value: string; // e.g. "2023-01-01|2023-03-31"
}

export function generateQuartersSincePublish(
  publishDateISO: string,
): QuarterOption[] {
  const quarters: QuarterOption[] = [];
  const publishDate = new Date(publishDateISO);
  const currentDate = new Date();

  // Start from the publish date's quarter
  let startDate = new Date(publishDate);
  startDate.setMonth(Math.floor(startDate.getMonth() / 3) * 3, 1);
  startDate.setHours(0, 0, 0, 0);

  while (startDate <= currentDate) {
    const endDate = new Date(startDate);
    endDate.setMonth(startDate.getMonth() + 3, 0); // Last day of quarter
    endDate.setHours(23, 59, 59, 999);

    const year = startDate.getFullYear();
    const quarter = Math.floor(startDate.getMonth() / 3) + 1;

    // Format dates as YYYY-MM-DD
    const formatDate = (date: Date) => {
      return date.toISOString().split('T')[0];
    };

    quarters.push({
      label: `${year} Q${quarter}`,
      value: `${formatDate(startDate)}|${formatDate(endDate)}`,
    });

    // Move to next quarter
    startDate.setMonth(startDate.getMonth() + 3);
  }

  return quarters;
}

/**
 * Debounce a value - delays updating the returned value until after the specified delay
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds
 * @returns The debounced value
 */
export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cleanup timeout on unmount or when value/delay changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Get date range for quarter filtering
 * Returns null values for all-time periods to ensure proper analytics handling
 */
export function getDateRangeForQuarter(
  showAllTime: boolean,
  year: string,
  quarter: string,
): { startDate: string | null; endDate: string | null } {
  if (showAllTime) {
    return {
      startDate: null,
      endDate: null,
    };
  }

  const quarterRanges = {
    Q1: { start: '01-01', end: '03-31' },
    Q2: { start: '04-01', end: '06-30' },
    Q3: { start: '07-01', end: '09-30' },
    Q4: { start: '10-01', end: '12-31' },
  };

  const range = quarterRanges[quarter as keyof typeof quarterRanges];
  if (!range) {
    // Fallback to Q1 if invalid quarter
    const q1Range = quarterRanges.Q1;
    return {
      startDate: `${year}-${q1Range.start}`,
      endDate: `${year}-${q1Range.end}`,
    };
  }

  return {
    startDate: `${year}-${range.start}`,
    endDate: `${year}-${range.end}`,
  };
}

/**
 * Convert relative dates from social media platforms to proper date format
 * Handles dates like "2 days ago", "1 month ago" and converts them to standard format
 * @param dateText - The relative date text to convert
 * @param timezone - System timezone (e.g., 'America/Boise')
 * @returns Formatted date string in system timezone format
 */
export function formatPublishedDate(
  dateText: string,
  timezone: string = 'America/Boise'
): string {
  if (!dateText || dateText === 'Unknown') return 'Unknown';

  // If it's already a proper date, format it
  if (dateText.includes('2023') || dateText.includes('2024') || dateText.includes('2025')) {
    try {
      return new Date(dateText).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: timezone,
      });
    } catch {
      return dateText;
    }
  }

  // For relative dates like "2 days ago", "1 month ago", calculate actual date
  const now = new Date();
  let actualDate = new Date(now);

  if (dateText.includes('hour')) {
    const hours = parseInt(dateText.match(/\d+/)?.[0] || '0');
    actualDate = new Date(now.getTime() - hours * 60 * 60 * 1000);
  } else if (dateText.includes('day')) {
    const days = parseInt(dateText.match(/\d+/)?.[0] || '0');
    actualDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  } else if (dateText.includes('week')) {
    const weeks = parseInt(dateText.match(/\d+/)?.[0] || '0');
    actualDate = new Date(now.getTime() - weeks * 7 * 24 * 60 * 60 * 1000);
  } else if (dateText.includes('month')) {
    const months = parseInt(dateText.match(/\d+/)?.[0] || '0');
    actualDate = new Date(now);
    actualDate.setMonth(now.getMonth() - months);
  } else if (dateText.includes('year')) {
    const years = parseInt(dateText.match(/\d+/)?.[0] || '0');
    actualDate = new Date(now);
    actualDate.setFullYear(now.getFullYear() - years);
  }

  return actualDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
  });
}

/**
 * Format a date using the system's configured timezone
 * @param date - Date to format
 * @param timezone - System timezone (e.g., 'America/Boise')
 * @param options - Formatting options
 */
export function formatDateInSystemTimezone(
  date: Date | string | number,
  timezone: string = 'America/Boise',
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }
): string {
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }

    return dateObj.toLocaleDateString('en-US', {
      ...options,
      timeZone: timezone,
    });
  } catch (error) {
    console.error('Error formatting date in system timezone:', error);
    return 'N/A';
  }
}

/**
 * Formats a date-only value stored as UTC midnight (e.g. a post publish date or
 * quarter boundary) without shifting it into another timezone.
 */
export function formatCalendarDate(
  date: Date | string | number,
  options?: Intl.DateTimeFormatOptions,
): string {
  return formatDateInSystemTimezone(date, 'UTC', options);
}

/**
 * Generates an appropriate empty state message that checks if the selected period
 * is before the friend's creation date
 */
export function getEmptyStateMessage(
  friend: { createdAt?: Date | string },
  selectedYear: string,
  selectedQuarter: string,
  defaultMessage: string,
  systemTimezone: string = 'America/Boise'
): string {
  // Check if the selected period is before the friend was created
  if (!friend.createdAt) {
    return defaultMessage;
  }

  const dateRange = getDateRangeForQuarter(
    false,
    selectedYear,
    selectedQuarter,
  );
  const quarterEndDate = new Date(dateRange.endDate || `${selectedYear}-12-31`);
  const friendCreatedAt = new Date(friend.createdAt);

  if (quarterEndDate < friendCreatedAt) {
    const formattedCreatedDate = formatDateInSystemTimezone(
      friendCreatedAt,
      systemTimezone
    );
    return `Friend since ${formattedCreatedDate}. No data available for this period.`;
  }

  return defaultMessage;
}

// Utility function to format numbers with K/M suffixes
export function formatNumberShort(
  num: number,
  useDecimal: boolean = false,
): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1000) {
    const decimals = useDecimal ? 1 : 0;
    return (num / 1000).toFixed(decimals).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
}